import { PlanogramProductSchema } from "@labeling-tools/api/atlas";
import { getPlanoDimension, getLabelDimension } from "../utils/plano-related.utils";
import Color from "color";
import ColorHash from "color-hash";
import { useRef, useCallback } from "react";

const colorHash = new ColorHash({ saturation: 0.9 });

const MINIMAP_WIDTH = 200;
const MINIMAP_HEIGHT = 120;

interface Props {
  planogramProducts: PlanogramProductSchema[];
  /** Current transform state from the main viewer */
  transformState: { positionX: number; positionY: number; scale: number };
  /** Size of the main viewer container */
  containerSize: { width: number; height: number };
  /** Called when the user drags on the minimap to pan the main viewer */
  onPan?: (positionX: number, positionY: number) => void;
}

export const PlanogramMinimap = ({ planogramProducts, transformState, containerSize, onPan }: Props) => {
  const planoDimension = getPlanoDimension(planogramProducts);
  const dragging = useRef(false);

  if (!planoDimension.w || !planoDimension.h) return null;

  // Scale so the planogram content fills the minimap width
  const planoAR = planoDimension.w / planoDimension.h;
  const containerAR = containerSize.width / containerSize.height;
  let contentW: number, contentH: number, offsetX: number, offsetY: number;
  if (planoAR >= containerAR) {
    contentW = containerSize.width;
    contentH = containerSize.width / planoAR;
    offsetX = 0;
    offsetY = (containerSize.height - contentH) / 2;
  } else {
    contentH = containerSize.height;
    contentW = containerSize.height * planoAR;
    offsetX = (containerSize.width - contentW) / 2;
    offsetY = 0;
  }

  // Scale based on planogram content width to fill the minimap
  const mmScale = MINIMAP_WIDTH / contentW;
  const planoW = contentW * mmScale;
  const planoH = contentH * mmScale;
  const planoLeft = 0;
  const planoTop = (MINIMAP_HEIGHT - planoH) / 2;

  // Offset for converting container coordinates to minimap coordinates
  const mmOffsetX = -offsetX * mmScale;
  const mmOffsetY = planoTop - offsetY * mmScale;

  const { positionX, positionY, scale } = transformState;
  const viewLeft = -positionX / scale;
  const viewTop = -positionY / scale;
  const viewWidth = containerSize.width / scale;
  const viewHeight = containerSize.height / scale;

  const vpLeft = mmOffsetX + viewLeft * mmScale;
  const vpTop = mmOffsetY + viewTop * mmScale;
  const vpWidth = viewWidth * mmScale;
  const vpHeight = viewHeight * mmScale;

  /** Convert a mouse position on the minimap to main viewer positionX/positionY */
  const minimapToPosition = (clientX: number, clientY: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    // Center the viewport on the clicked point
    const contentX = (mx - mmOffsetX) / mmScale;
    const contentY = (my - mmOffsetY) / mmScale;
    const newPosX = -(contentX - containerSize.width / scale / 2) * scale;
    const newPosY = -(contentY - containerSize.height / scale / 2) * scale;
    return { newPosX, newPosY };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    if (onPan) {
      const { newPosX, newPosY } = minimapToPosition(e.clientX, e.clientY, e.currentTarget);
      onPan(newPosX, newPosY);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current || !onPan) return;
    e.preventDefault();
    e.stopPropagation();
    const { newPosX, newPosY } = minimapToPosition(e.clientX, e.clientY, e.currentTarget);
    onPan(newPosX, newPosY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  return (
    <div
      className="absolute bottom-2 right-2 z-[100] rounded shadow-lg border border-gray-300 bg-gray-200 overflow-hidden cursor-crosshair"
      style={{ width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Product boxes positioned within the planogram area */}
      <div className="absolute pointer-events-none" style={{ left: planoLeft, top: planoTop, width: planoW, height: planoH }}>
        {planogramProducts.map((product) => {
          const labelDim = getLabelDimension(product);
          const left = ((labelDim.x0 - planoDimension.x0) / planoDimension.w) * 100 + "%";
          const top = ((labelDim.y0 - planoDimension.y0) / planoDimension.h) * 100 + "%";
          const width = (labelDim.w / planoDimension.w) * 100 + "%";
          const height = (labelDim.h / planoDimension.h) * 100 + "%";
          const bgColor = colorHash.hex(product.description);
          const backgroundColor = Color(bgColor).alpha(0.4).toString();

          return (
            <div
              key={product.id}
              className="absolute"
              style={{ left, top, width, height, backgroundColor }}
            />
          );
        })}
      </div>

      {/* Viewport indicator */}
      <div
        className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none"
        style={{
          left: vpLeft,
          top: vpTop,
          width: vpWidth,
          height: vpHeight,
        }}
      />
    </div>
  );
};
